import { z } from 'zod'
import { ReservationF } from '../../types/reservation-detail.types'
import { TFunction } from 'i18next'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { useForm, SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

export default function ReservationTypeFormHook({ reservation, onSubmit }: { reservation: ReservationF; onSubmit: (data: ReservationTypeFormSchema) => Promise<void> }) {
  const { t } = useTranslation()
  const reservationTypeSchemaWithTranslation = getReservationTypeFormSchema(t)

  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<ReservationTypeFormSchema>({ resolver: zodResolver(reservationTypeSchemaWithTranslation) })

  const submitHandler: SubmitHandler<ReservationTypeFormSchema> = useCallback(
    async (data: ReservationTypeFormSchema) => {
      await onSubmit(data)
    },
    [onSubmit],
  )
  const onFormSubmit = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler])

  useEffect(() => {
    setValue('isGroup', reservation.isGroup)
    setValue('name', reservation.group ?? null)
  }, [])

  const formValue = watch()

  return { onFormSubmit, control, errors, formValue }
}

function getReservationTypeFormSchema(t?: TFunction) {
  return z
    .object({
      isGroup: z.boolean(),
      name: z.string().nullable(),
    })
    .superRefine((data, ctx) => {
      if (data.isGroup && !data.name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['name'],
        })
      }
    })
}

const reservationTypeFormSchema = getReservationTypeFormSchema()
export type ReservationTypeFormSchema = z.infer<typeof reservationTypeFormSchema>
